export const CLUB_CALCULATOR_CONFIG = {
  weeksPerMonth: 4.33,

  fees: {
    stripePercent: 0.01,
    stripeMinimum: 0.2,
    platformPercent: 0.01,
    platformMinimum: 0.2,
  },

  plans: {
    FREE: { price: 0, maxMembers: 10, hasPayments: false },
    STANDARD: { price: 4, maxMembers: 25, hasPayments: true },
    PRO: { price: 8, maxMembers: 100, hasPayments: true },
    EXTRA: { price: 12, maxMembers: 100, hasPayments: true },
  },

  estimatedSessionAdminMinutesSaved: {
    FREE: 2,
    STANDARD: 5,
    PRO: 7,
    EXTRA: 9,
  },

  estimatedPaymentAdminMinutesPerEvent: {
    card_per_session: 0.4,
    monthly_membership: 0.3,
    shuttleOps: {
      STANDARD: 0.15,
      PRO: 0.05,
      EXTRA: 0.05,
    },
  },

  estimatedCommunicationMinutesSaved: {
    EXTRA: 45,
  },
};

export const CLUB_CALCULATOR_DEFAULTS = {
  members: 24,
  sessionsPerWeek: 3,
  playersPerSession: 12,
  sessionFee: 5,
  adminMinutesPerSession: 15,
  hourlyAdminValue: 12,
  currentPaymentMethod: 'bank_transfer',
  currentProcessorFeePerPayment: null,
  shuttleOpsFeePayer: 'players',
  bankTransferReconciliationMinutes: 1.5,
  avgSessionsPerPlayerPerMonth: 4,
  recoveredPaymentsPerMonth: 1,
  recoveredPaymentValue: 5,
  wantsWhatsApp: false,
};

export const PAYMENT_METHODS = [
  'bank_transfer',
  'card_per_session',
  'mixed',
  'monthly_membership',
];

export const FEE_PAYERS = ['players', 'club'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const getShuttleOpsTransactionCost = (amount) => {
  const {
    stripePercent,
    stripeMinimum,
    platformPercent,
    platformMinimum,
  } = CLUB_CALCULATOR_CONFIG.fees;

  const stripeFee = Math.max(amount * stripePercent, stripeMinimum);
  const platformFee = Math.max(amount * platformPercent, platformMinimum);

  return stripeFee + platformFee;
};

export const getRecommendedPlan = ({ members, wantsWhatsApp }) => {
  if (members > CLUB_CALCULATOR_CONFIG.plans.EXTRA.maxMembers) return null;
  if (wantsWhatsApp) return 'EXTRA';
  if (members <= CLUB_CALCULATOR_CONFIG.plans.FREE.maxMembers) return 'FREE';
  if (members <= CLUB_CALCULATOR_CONFIG.plans.STANDARD.maxMembers) return 'STANDARD';
  return 'PRO';
};

const getCurrentPaymentScenario = ({
  currentPaymentMethod,
  monthlyAttendances,
  estimatedActivePlayers,
  avgSessionsPerPlayerPerMonth,
  currentProcessorFeePerPayment,
  bankTransferReconciliationMinutes,
}) => {
  if (!PAYMENT_METHODS.includes(currentPaymentMethod)) {
    throw new Error(`Unknown payment method: ${currentPaymentMethod}`);
  }

  if (currentPaymentMethod === 'bank_transfer') {
    return {
      paymentEvents: monthlyAttendances,
      transactionCost: 0,
      paymentAdminMinutes: monthlyAttendances * bankTransferReconciliationMinutes,
    };
  }

  let paymentEvents;
  let adminMinutesPerEvent;

  if (currentPaymentMethod === 'card_per_session') {
    paymentEvents = monthlyAttendances;
    adminMinutesPerEvent = CLUB_CALCULATOR_CONFIG.estimatedPaymentAdminMinutesPerEvent.card_per_session;
  }

  if (currentPaymentMethod === 'mixed') {
    const payAsYouGoPlayers = estimatedActivePlayers / 2;
    const monthlyPlayers = estimatedActivePlayers - payAsYouGoPlayers;
    paymentEvents =
      payAsYouGoPlayers * avgSessionsPerPlayerPerMonth + monthlyPlayers;
    adminMinutesPerEvent =
      (bankTransferReconciliationMinutes +
        CLUB_CALCULATOR_CONFIG.estimatedPaymentAdminMinutesPerEvent.card_per_session) /
      2;
  }

  if (currentPaymentMethod === 'monthly_membership') {
    paymentEvents = estimatedActivePlayers;
    adminMinutesPerEvent = CLUB_CALCULATOR_CONFIG.estimatedPaymentAdminMinutesPerEvent.monthly_membership;
  }

  const hasUserSuppliedProcessorFee = Number.isFinite(currentProcessorFeePerPayment);

  return {
    paymentEvents,
    transactionCost: hasUserSuppliedProcessorFee
      ? paymentEvents * currentProcessorFeePerPayment
      : null,
    paymentAdminMinutes: paymentEvents * adminMinutesPerEvent,
  };
};

const getPlanExplanation = ({
  recommendedPlan,
  monthlySessions,
  monthlyAttendances,
  bankTransferReconciliationMinutes,
}) => {
  const intro = `Your club runs about ${monthlySessions.toFixed(1)} sessions and ${monthlyAttendances.toFixed(0)} attendance charges each month.`;

  if (recommendedPlan === 'FREE') {
    return `${intro} Free fits clubs with up to 10 members. It does not include payment features, so this estimate keeps your current payment process unchanged.`;
  }

  if (recommendedPlan === 'STANDARD') {
    return `${intro} Standard fits clubs with up to 25 members and unlocks session payments. The estimate uses ${bankTransferReconciliationMinutes.toFixed(1)} minutes to check each bank transfer when relevant.`;
  }

  if (recommendedPlan === 'EXTRA') {
    return `${intro} Extra fits clubs with up to 100 members. It adds WhatsApp reminders and assistant features to Pro's monthly payment batching.`;
  }

  return `${intro} Pro fits clubs with up to 100 members and groups session charges into monthly player payment batches.`;
};

export const calculateClubSavings = (overrides = {}) => {
  const inputs = { ...CLUB_CALCULATOR_DEFAULTS, ...overrides };
  const {
    members,
    sessionsPerWeek,
    playersPerSession,
    sessionFee,
    adminMinutesPerSession,
    hourlyAdminValue,
    currentPaymentMethod,
    currentProcessorFeePerPayment,
    shuttleOpsFeePayer,
    bankTransferReconciliationMinutes,
    avgSessionsPerPlayerPerMonth,
    recoveredPaymentsPerMonth,
    recoveredPaymentValue,
    wantsWhatsApp,
  } = inputs;

  if (!FEE_PAYERS.includes(shuttleOpsFeePayer)) {
    throw new Error(`Unknown fee payer: ${shuttleOpsFeePayer}`);
  }

  const monthlySessions = sessionsPerWeek * CLUB_CALCULATOR_CONFIG.weeksPerMonth;
  const monthlyAttendances = monthlySessions * playersPerSession;
  const estimatedActivePlayers = clamp(
    Math.round(monthlyAttendances / Math.max(avgSessionsPerPlayerPerMonth, 1)),
    Math.min(playersPerSession, members),
    members
  );

  const current = getCurrentPaymentScenario({
    currentPaymentMethod,
    monthlyAttendances,
    estimatedActivePlayers,
    avgSessionsPerPlayerPerMonth,
    currentProcessorFeePerPayment,
    bankTransferReconciliationMinutes,
  });

  const currentSessionAdminMinutes = monthlySessions * adminMinutesPerSession;
  const currentTotalAdminMinutes = currentSessionAdminMinutes + current.paymentAdminMinutes;

  const calculatePlanScenario = (plan) => {
    const planConfig = CLUB_CALCULATOR_CONFIG.plans[plan];
    const sessionAdminAfterPerSession = Math.max(
      adminMinutesPerSession -
        CLUB_CALCULATOR_CONFIG.estimatedSessionAdminMinutesSaved[plan],
      1
    );
    const shuttleOpsSessionAdminMinutes = monthlySessions * sessionAdminAfterPerSession;

    if (!planConfig.hasPayments) {
      const adminMinutesSaved = Math.max(
        currentSessionAdminMinutes - shuttleOpsSessionAdminMinutes,
        0
      );
      const adminHoursSaved = adminMinutesSaved / 60;
      const adminValueSaved = adminHoursSaved * hourlyAdminValue;

      return {
        plan,
        planPrice: planConfig.price,
        shuttleOpsPaymentEvents: current.paymentEvents,
        shuttleOpsTransactionCost: current.transactionCost,
        shuttleOpsPaymentAdminMinutes: current.paymentAdminMinutes,
        shuttleOpsSessionAdminMinutes,
        adminMinutesSaved,
        adminHoursSaved,
        adminValueSaved,
        recoveredPaymentValueTotal: 0,
        grossMonthlyValue: adminValueSaved,
        shuttleOpsClubPaymentFees: 0,
        shuttleOpsPlayerPaymentFees: 0,
        totalClubCost: planConfig.price,
        paymentCostDifference: 0,
        netMonthlyBenefit: adminValueSaved,
        breakEvenAdminMinutes: 0,
        breakEvenRecoveredPaymentCount: 0,
      };
    }

    const usesMonthlyBatches = plan === 'PRO' || plan === 'EXTRA';
    const shuttleOpsPaymentEvents = usesMonthlyBatches
      ? estimatedActivePlayers
      : monthlyAttendances;
    const amountPerPayment = usesMonthlyBatches
      ? sessionFee * avgSessionsPerPlayerPerMonth
      : sessionFee;
    const shuttleOpsTransactionCost =
      shuttleOpsPaymentEvents * getShuttleOpsTransactionCost(amountPerPayment);
    const shuttleOpsClubPaymentFees = shuttleOpsFeePayer === 'club'
      ? shuttleOpsTransactionCost
      : 0;
    const shuttleOpsPlayerPaymentFees = shuttleOpsFeePayer === 'players'
      ? shuttleOpsTransactionCost
      : 0;
    const shuttleOpsPaymentAdminMinutes =
      shuttleOpsPaymentEvents *
      CLUB_CALCULATOR_CONFIG.estimatedPaymentAdminMinutesPerEvent.shuttleOps[plan];
    const communicationMinutesSaved =
      CLUB_CALCULATOR_CONFIG.estimatedCommunicationMinutesSaved[plan] || 0;
    const shuttleOpsTotalAdminMinutes =
      shuttleOpsSessionAdminMinutes + shuttleOpsPaymentAdminMinutes;
    const adminMinutesSaved = Math.max(
      currentTotalAdminMinutes - shuttleOpsTotalAdminMinutes + communicationMinutesSaved,
      0
    );
    const adminHoursSaved = adminMinutesSaved / 60;
    const adminValueSaved = adminHoursSaved * hourlyAdminValue;
    const recoveredPaymentValueTotal =
      Math.max(recoveredPaymentsPerMonth, 0) * Math.max(recoveredPaymentValue, 0);
    const grossMonthlyValue = adminValueSaved + recoveredPaymentValueTotal;
    const paymentCostDifference = Number.isFinite(current.transactionCost)
      ? current.transactionCost - shuttleOpsClubPaymentFees
      : null;
    const netMonthlyBenefit = Number.isFinite(paymentCostDifference)
      ? grossMonthlyValue + paymentCostDifference - planConfig.price
      : null;
    const netClubCostToCover = Number.isFinite(current.transactionCost)
      ? Math.max(planConfig.price + shuttleOpsClubPaymentFees - current.transactionCost, 0)
      : null;
    const breakEvenAdminMinutes = Number.isFinite(netClubCostToCover) && hourlyAdminValue > 0
      ? (netClubCostToCover / hourlyAdminValue) * 60
      : null;
    const breakEvenRecoveredPaymentCount =
      Number.isFinite(netClubCostToCover) && recoveredPaymentValue > 0
        ? netClubCostToCover / recoveredPaymentValue
        : null;

    return {
      plan,
      planPrice: planConfig.price,
      shuttleOpsPaymentEvents,
      shuttleOpsTransactionCost,
      shuttleOpsClubPaymentFees,
      shuttleOpsPlayerPaymentFees,
      shuttleOpsPaymentAdminMinutes,
      shuttleOpsSessionAdminMinutes,
      adminMinutesSaved,
      adminHoursSaved,
      adminValueSaved,
      recoveredPaymentValueTotal,
      grossMonthlyValue,
      totalClubCost: planConfig.price + shuttleOpsClubPaymentFees,
      paymentCostDifference,
      netMonthlyBenefit,
      breakEvenAdminMinutes,
      breakEvenRecoveredPaymentCount,
    };
  };

  const scenarios = Object.fromEntries(
    Object.keys(CLUB_CALCULATOR_CONFIG.plans).map((plan) => [
      plan,
      calculatePlanScenario(plan),
    ])
  );
  const recommendedPlan = getRecommendedPlan({ members, wantsWhatsApp });
  const chosen = recommendedPlan ? scenarios[recommendedPlan] : null;

  return {
    inputs,
    monthlySessions,
    monthlyAttendances,
    estimatedActivePlayers,
    currentPaymentEvents: current.paymentEvents,
    currentTransactionCost: current.transactionCost,
    currentPaymentAdminMinutes: current.paymentAdminMinutes,
    currentSessionAdminMinutes,
    currentTotalAdminMinutes,
    recommendedPlan,
    isAbovePublicLimit: recommendedPlan === null,
    explanation: recommendedPlan
      ? getPlanExplanation({
          recommendedPlan,
          monthlySessions,
          monthlyAttendances,
          bankTransferReconciliationMinutes,
        })
      : members <= 150
        ? 'Pro and Extra include up to 100 members. A capacity bolt-on supports clubs with up to 150 members. Request a demo for the correct setup and price.'
        : 'ShuttleOps has no hard product-wide member limit. Request a demo to discuss capacity and available bolt-ons for your club.',
    scenarios,
    ...(chosen || {}),
  };
};
